import type { ApiErrorOptions } from '@glyphic/shared-core'
import { ApiError as SharedApiError, isApiSessionExpired } from '@glyphic/shared-core'

import { sanitizeUserFacingErrorMessage } from '../lib/userFacingErrorMessage'

/** 用户端 ApiError：叠加文案脱敏 */
export class ApiError extends SharedApiError {
  /** 全局鉴权拦截器已处理（勿再 Toast） */
  authHandled?: boolean

  retryAfterSeconds?: number

  constructor(
    message: string,
    code?: string,
    status?: number,
    detail?: string,
    retryAfterSeconds?: number,
  ) {
    const options: ApiErrorOptions = {
      code,
      status,
      detail,
      sanitizeMessage: sanitizeUserFacingErrorMessage
    }
    super(message, options)
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

/** 会话失效（与 {@link isApiSessionExpired} 对齐，作用于 {@link ApiError}） */
export function isUnauthorizedApiError(error: unknown): boolean {
  if (!isApiError(error)) return false
  return isApiSessionExpired(error.code, error.status)
}

/** 页面 catch 中先调用，避免吞掉全局登出后的 401 */
export function rethrowIfUnauthorized(error: unknown): void {
  if (isUnauthorizedApiError(error)) throw error
}

export function markApiErrorAuthHandled(error: unknown): void {
  if (error instanceof ApiError) {
    error.authHandled = true
  }
}

/** 页面级 catch 是否还应弹出 Toast */
export function shouldShowApiErrorToast(error: unknown): boolean {
  if (isUnauthorizedApiError(error)) return false
  if (error instanceof ApiError && error.authHandled) return false
  return true
}

/** fetch 因网络不可达、后端未启动等未能建立连接 */
export function isNetworkRequestError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const msg = error.message.trim().toLowerCase()
  if (msg === 'failed to fetch') return true
  if (msg === 'networkerror when attempting to fetch resource.') return true
  if (msg === 'load failed') return true
  return error instanceof TypeError && /fetch|network/i.test(msg)
}
